import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calendar,
  User,
  Target,
  CheckCircle,
  Clock,
  PlayCircle,
  Trophy,
  ArrowRight,
  BookOpen,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import type { SessionWithProfessional } from '../../../shared/session-types';

export default function PatientSessionsPage() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<SessionWithProfessional[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'todas' | 'pendiente' | 'en_curso' | 'finalizada'>('todas');

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      const response = await api.get('/patient/sessions');
      setSessions(response.data.data);
    } catch (error) {
      console.error('Error loading sessions:', error);
      toast.error('Error al cargar las sesiones');
    } finally {
      setLoading(false);
    }
  };

  const handleStartSession = (sessionId: number) => {
    navigate(`/paciente/sesiones/${sessionId}`);
  };

  const filteredSessions = filter === 'todas'
    ? sessions
    : sessions.filter(s => s.estado === filter);

  const getStatusColor = (estado: string) => {
    switch (estado) {
      case 'pendiente':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'en_curso':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'finalizada':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'cancelada':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getStatusIcon = (estado: string) => {
    switch (estado) {
      case 'pendiente':
        return <Clock className="w-4 h-4" />;
      case 'en_curso':
        return <PlayCircle className="w-4 h-4" />;
      case 'finalizada':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Calendar className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20">
      {/* Header */}
      <div className="glass-container mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">Mis Sesiones</h1>
            <p className="text-gray-400">
              Sesiones asignadas por tu profesional
            </p>
          </div>
          <BookOpen className="w-12 h-12 text-blue-400 opacity-50" />
        </div>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="glass-container">
          <div className="flex items-center gap-2 mb-1">
            <Calendar className="w-4 h-4 text-gray-400" />
            <p className="text-sm text-gray-400">Total</p>
          </div>
          <p className="text-2xl font-bold">{sessions.length}</p>
        </div>
        <div className="glass-container">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-blue-400" />
            <p className="text-sm text-gray-400">Pendientes</p>
          </div>
          <p className="text-2xl font-bold text-blue-400">
            {sessions.filter(s => s.estado === 'pendiente').length}
          </p>
        </div>
        <div className="glass-container">
          <div className="flex items-center gap-2 mb-1">
            <PlayCircle className="w-4 h-4 text-yellow-400" />
            <p className="text-sm text-gray-400">En Curso</p>
          </div>
          <p className="text-2xl font-bold text-yellow-400">
            {sessions.filter(s => s.estado === 'en_curso').length}
          </p>
        </div>
        <div className="glass-container">
          <div className="flex items-center gap-2 mb-1">
            <Trophy className="w-4 h-4 text-green-400" />
            <p className="text-sm text-gray-400">Completadas</p>
          </div>
          <p className="text-2xl font-bold text-green-400">
            {sessions.filter(s => s.estado === 'finalizada').length}
          </p>
        </div>
      </div>

      {/* Filtros */}
      <div className="glass-container mb-6">
        <div className="flex flex-wrap gap-2">
          {['todas', 'pendiente', 'en_curso', 'finalizada'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f as any)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                filter === f
                  ? 'bg-blue-500/30 text-blue-400 border-2 border-blue-500'
                  : 'bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Lista de Sesiones */}
      {filteredSessions.length === 0 ? (
        <div className="glass-container text-center py-12">
          <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-400 text-lg">
            {filter === 'todas'
              ? 'No tienes sesiones asignadas aún'
              : `No hay sesiones en estado "${filter}"`}
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Tu profesional te asignará sesiones para trabajar desde casa
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredSessions.map((session) => (
            <motion.div
              key={session.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass-container hover:bg-white/10 transition-all cursor-pointer"
              onClick={() => handleStartSession(session.id)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-semibold">
                      Sesión #{session.id}
                    </h3>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium border flex items-center gap-1 ${getStatusColor(
                        session.estado
                      )}`}
                    >
                      {getStatusIcon(session.estado)}
                      {session.estado}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <User className="w-4 h-4" />
                    <span>
                      Profesional: {session.profesional_nombre}{' '}
                      {session.profesional_apellidos}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-400 mt-1">
                    <Calendar className="w-4 h-4" />
                    <span>
                      Creada: {new Date(session.fecha_creacion).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <ArrowRight className="w-6 h-6 text-gray-400" />
              </div>

              {/* Objetivos */}
              {session.objetivos && (
                <div className="mb-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
                  <div className="flex items-center gap-2 mb-1">
                    <Target className="w-4 h-4 text-blue-400" />
                    <p className="text-sm font-medium text-blue-400">Objetivos</p>
                  </div>
                  <p className="text-sm text-gray-300">{session.objetivos}</p>
                </div>
              )}

              {/* Progreso */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-400">Progreso</span>
                  <span className="font-medium">
                    {session.juegos_completados} / {session.total_juegos} juegos
                  </span>
                </div>
                <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{
                      width: `${
                        session.total_juegos > 0
                          ? (session.juegos_completados / session.total_juegos) * 100
                          : 0
                      }%`,
                    }}
                    transition={{ duration: 0.5 }}
                    className="h-full bg-gradient-to-r from-blue-500 to-purple-500"
                  />
                </div>
                <p className="text-xs text-gray-500 text-right">
                  {session.total_juegos > 0
                    ? Math.round((session.juegos_completados / session.total_juegos) * 100)
                    : 0}
                  % completado
                </p>
              </div>

              {/* Botón de acción */}
              {session.estado !== 'finalizada' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStartSession(session.id);
                  }}
                  className="w-full mt-4 glass-button px-6 py-3 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 font-semibold flex items-center justify-center gap-2"
                >
                  <PlayCircle className="w-5 h-5" />
                  {session.estado === 'pendiente' ? 'Comenzar Sesión' : 'Continuar Sesión'}
                </button>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
